import { useCallback, useRef, type RefObject } from 'react';
import moment from 'moment';
import {
  ChartModifierBase2D,
  CursorModifier,
  DpiHelper,
  EAutoRange,
  EAxisAlignment,
  EXyDirection,
  FastBandRenderableSeries,
  FastLineRenderableSeries,
  type ModifierMouseArgs,
  MouseWheelZoomModifier,
  NumberRange,
  NumericAxis,
  type NumericLabelProvider,
  XyDataSeries,
  XyyDataSeries,
  ZoomExtentsModifier,
  ZoomPanModifier,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { escape as escapeHtml } from 'lodash-es';
import { formatNumber } from 'utils/number';
import styles from './price-chart.module.scss';

// A plotted point / band point is [epoch-ms, value]; the price engine keys everything on ms.
export type LinePoint = [number, number];

// One drawn line — a settle line, a forward strip (dashed), or a vol smile line.
export interface PriceLine {
  key: string;
  name: string; // human series label (curve/contract) — shown in the hover tooltip
  color: string;
  axis: 'price' | 'vol';
  dash?: boolean;
  width?: number;
  points: LinePoint[];
}

// A ±σ risk cone drawn as a shaded band between the hi/lo envelopes (price axis only).
export interface PriceBand {
  key: string;
  color: string;
  hi: LinePoint[];
  lo: LinePoint[];
}

export interface PricePlot {
  lines: PriceLine[];
  bands: PriceBand[];
  hasVol: boolean; // a secondary right-hand axis is needed
  priceDec: number; // decimals for the price axis labels
  yLabel: string;
}

interface Props {
  plot: PricePlot;
  height?: number | string;
}

const withAlpha = (hex: string, alpha: number): string => {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
};

const monthLabel = (ms: number): string => moment.utc(ms).format("MMM 'YY");

// Marker dot drawn on each hovered line — a filled disc in the series color with a white ring
// so it reads on top of the line. Positioned via inline left/top on the overlay layer.
const dotSpan = (left: number, top: number, color: string): string =>
  `<span style="position:absolute;left:${left}px;top:${top}px;width:9px;height:9px;` +
  `margin:-4.5px 0 0 -4.5px;border-radius:50%;background:${color};` +
  `border:1.5px solid #fff;box-shadow:0 0 0 1px rgb(0 0 0 / 15%)"></span>`;

// Hover tooltip: on mouse-move, hit-test every named line at the cursor's x, drop a marker dot on
// each line at that tenor, and write a small HTML card — the tenor date plus one "● curve: value"
// row per series — into an absolutely-positioned overlay div. Mirrors the app's chart convention
// (a ChartModifierBase2D writing into a div; no native SciChart tooltip). ±σ bands and any unnamed
// series are skipped.
//
// SciChart works in device pixels premultiplied by DpiHelper.PIXEL_RATIO, but the overlay layer is
// laid out in CSS pixels — so every screen coordinate (the mouse, and each hit point's xCoord/yCoord,
// which are relative to the seriesViewRect) is divided by the ratio before it drives left/top. Using
// the device coordinates directly is what put the card far from the line and drifting on retina.
class PriceHoverTooltipModifier extends ChartModifierBase2D {
  readonly type = 'PriceHoverTooltip';

  private readonly tooltipRef: RefObject<HTMLDivElement | null>;

  private readonly dotsRef: RefObject<HTMLDivElement | null>;

  constructor(
    tooltipRef: RefObject<HTMLDivElement | null>,
    dotsRef: RefObject<HTMLDivElement | null>
  ) {
    super();
    this.tooltipRef = tooltipRef;
    this.dotsRef = dotsRef;
  }

  private clear(): void {
    if (this.tooltipRef.current) this.tooltipRef.current.style.display = 'none';
    if (this.dotsRef.current) this.dotsRef.current.innerHTML = '';
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    const el = this.tooltipRef.current;
    const dots = this.dotsRef.current;
    if (!el || !dots || !this.parentSurface) return;

    const dpr = DpiHelper.PIXEL_RATIO || 1;
    const vr = this.parentSurface.seriesViewRect;
    const { x, y } = args.mousePoint;
    const rows: string[] = [];
    const markers: string[] = [];
    const seen = new Set<string>();
    let dateMs: number | null = null;
    let sliceLeft: number | null = null;

    for (const s of this.parentSurface.renderableSeries.asArray()) {
      if (!s.isVisible) continue;
      const ds = s.dataSeries as XyDataSeries;
      const name = ds?.dataSeriesName;
      if (!name) continue; // skip ±σ bands / unnamed series
      const hit = s.hitTestProvider.hitTestXSlice(x, y, 12);
      if (!hit.isHit || hit.dataSeriesIndex < 0 || seen.has(name)) continue;
      seen.add(name);
      if (dateMs === null) dateMs = hit.xValue;
      const stroke = (s as FastLineRenderableSeries).stroke ?? '#333';

      // Data point in CSS px: hit.xCoord/yCoord are seriesViewRect-relative and DPR-scaled.
      const dotLeft = (vr.left + hit.xCoord) / dpr;
      const dotTop = (vr.top + hit.yCoord) / dpr;
      if (sliceLeft === null) sliceLeft = dotLeft;
      markers.push(dotSpan(dotLeft, dotTop, stroke));

      const value =
        s.yAxisId === 'vol'
          ? `${formatNumber(hit.yValue * 100, 1, 1, 'decimal', true)}%`
          : formatNumber(hit.yValue, 2, 2, 'decimal', true);
      rows.push(
        `<div><span style="color:${stroke}">●</span> ${escapeHtml(
          name
        )}: <b>${value}</b></div>`
      );
    }

    if (rows.length === 0 || dateMs === null) {
      this.clear();
      return;
    }

    dots.innerHTML = markers.join('');

    const date = moment.utc(dateMs).format('MMM YYYY');
    el.innerHTML = `<div style="font-weight:600;margin-bottom:2px">${date}</div>${rows.join(
      ''
    )}`;
    el.style.display = 'block';

    // Anchor the card just right of the crosshair, flipping left near the plot's right edge.
    const anchor = sliceLeft ?? x / dpr;
    const cardW = el.offsetWidth;
    const rightEdge = (vr.left + vr.width) / dpr;
    const left =
      anchor + 12 + cardW > rightEdge ? anchor - cardW - 12 : anchor + 12;
    el.style.left = `${Math.max(0, left)}px`;
    el.style.top = `${Math.max(0, y / dpr - 10)}px`;
  }

  override modifierMouseLeave(): void {
    this.clear();
  }
}

// SciChart Price Visualization chart: an epoch-ms X (month labels), a left price axis and an
// optional right volatility axis, N line series (settles solid, forward strips dashed, vol smile
// dashed by moneyness) plus shaded ±σ risk bands. The time axis zooms (mouse wheel) and pans (drag)
// while the price/vol axes auto-fit the visible data's full vertical extent; double-click zooms back
// out. A crosshair + hover tooltip (curve · date · value) follow the cursor. Drawn through the shared
// SciChartView wrapper (license/WASM/reset-zoom/export all handled there).
export function PriceChart({ plot, height = 460 }: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const xAxis = new NumericAxis(wasmContext, {
        id: 'xAxis',
        axisAlignment: EAxisAlignment.Bottom,
        drawMajorGridLines: false,
        growBy: new NumberRange(0.02, 0.02),
      });
      (xAxis.labelProvider as NumericLabelProvider).formatLabel = monthLabel;
      surface.xAxes.add(xAxis);

      // autoRange Always keeps each y axis fitted to the data currently in view (plus 10% padding),
      // so the full vertical extent is always visible — on first load, when the query changes, and
      // as the user zooms the time axis (price rescales to what's on screen).
      const priceAxis = new NumericAxis(wasmContext, {
        id: 'price',
        axisAlignment: EAxisAlignment.Left,
        growBy: new NumberRange(0.1, 0.1),
        autoRange: EAutoRange.Always,
      });
      surface.yAxes.add(priceAxis);

      const volAxis = new NumericAxis(wasmContext, {
        id: 'vol',
        axisAlignment: EAxisAlignment.Right,
        isVisible: false,
        growBy: new NumberRange(0.1, 0.1),
        autoRange: EAutoRange.Always,
      });
      (volAxis.labelProvider as NumericLabelProvider).formatLabel = (v) =>
        `${formatNumber(v * 100, 0, 0, 'decimal', true)}%`;
      surface.yAxes.add(volAxis);

      // Time-axis zoom/pan: mouse-wheel zoom + drag-pan, both X-only so the price/vol axes keep
      // auto-fitting the visible data vertically; double-click zooms back out to the full extent.
      // (The shared SciChartView also shows a Reset Zoom button once the view has been changed.)
      surface.chartModifiers.add(
        new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection })
      );
      surface.chartModifiers.add(
        new ZoomPanModifier({ xyDirection: EXyDirection.XDirection })
      );
      surface.chartModifiers.add(new ZoomExtentsModifier());

      // A dashed crosshair (CursorModifier, tooltip disabled) + the custom hover tooltip.
      surface.chartModifiers.add(
        new CursorModifier({
          showTooltip: false,
          showAxisLabels: false,
          crosshairStroke: '#a8a8a8',
          crosshairStrokeDashArray: [4, 4],
        })
      );
      surface.chartModifiers.add(
        new PriceHoverTooltipModifier(tooltipRef, dotsRef)
      );
    },
    []
  );

  const renderData = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      surface.renderableSeries.clear();

      const priceAxis = surface.yAxes.getById('price');
      (priceAxis.labelProvider as NumericLabelProvider).formatLabel = (v) =>
        formatNumber(v, plot.priceDec, plot.priceDec, 'decimal', true);
      priceAxis.axisTitle = plot.yLabel;

      const volAxis = surface.yAxes.getById('vol');
      volAxis.isVisible = plot.hasVol;

      plot.bands.forEach((b) => {
        if (b.hi.length === 0) return;
        surface.renderableSeries.add(
          new FastBandRenderableSeries(wasmContext, {
            dataSeries: new XyyDataSeries(wasmContext, {
              xValues: b.hi.map(([x]) => x),
              yValues: b.hi.map(([, y]) => y),
              y1Values: b.lo.map(([, y]) => y),
            }),
            fill: withAlpha(b.color, 0.12),
            fillY1: withAlpha(b.color, 0.12),
            stroke: 'transparent',
            strokeY1: 'transparent',
            strokeThickness: 0,
            xAxisId: 'xAxis',
            yAxisId: 'price',
          })
        );
      });

      plot.lines.forEach((ln) => {
        if (ln.points.length === 0) return;
        surface.renderableSeries.add(
          new FastLineRenderableSeries(wasmContext, {
            dataSeries: new XyDataSeries(wasmContext, {
              xValues: ln.points.map(([x]) => x),
              yValues: ln.points.map(([, y]) => y),
              dataSeriesName: ln.name,
            }),
            stroke: ln.color,
            strokeThickness: ln.width ?? 2,
            strokeDashArray: ln.dash ? [4, 4] : undefined,
            xAxisId: 'xAxis',
            yAxisId: ln.axis,
          })
        );
      });
    },
    [plot]
  );

  return (
    <SciChartView
      id="pricing-price-chart"
      initChart={initChart}
      renderData={renderData}
      renderDeps={[plot]}
      height={height}
      showResetZoom
    >
      <div ref={dotsRef} className={styles.markerLayer} />
      <div ref={tooltipRef} className={styles.tooltip} />
    </SciChartView>
  );
}
